import { Fragment, useState, useEffect, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  IconX,
  IconCheck,
  IconReceipt2,
  IconCash,
  IconBuildingBank,
  IconQrcode,
  IconCreditCard,
  IconAlertTriangle,
  IconInfoCircle,
} from '@tabler/icons-react';
import { toast } from 'react-toastify';
import { http, getAuthorizationHeader } from '../config/http';
import { useMasterData } from '../context/MasterDataContext';
import BankLogo from './BankLogo';

function numberWithCommas(x) {
  if (x === null || x === undefined || x === '') return '0';
  const num = parseInt(x, 10);
  if (isNaN(num)) return '0';
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export default function ModalKoreksiPembayaran({ isOpen, onClose, penjualan, onSuccess }) {
  const { bankList: masterBankList, paymentMethods } = useMasterData() || {};
  
  const bankList = useMemo(() => {
    return masterBankList && masterBankList.length > 0 ? masterBankList : ['BCA', 'BNI', 'BRI', 'Mandiri', 'BSI', 'Lainnya'];
  }, [masterBankList]);
  const defaultBank = bankList[0] || 'BCA';

  const effectivePaymentMethods = useMemo(() => {
    return paymentMethods || { cash: true, transfer: true, qris: true, edc: true };
  }, [paymentMethods]);

  const availableMethodIds = useMemo(() => {
    return ['cash', 'transfer', 'qris', 'edc'].filter((m) => effectivePaymentMethods[m] !== false);
  }, [effectivePaymentMethods]);

  const firstAvailableMethod = availableMethodIds[0] || 'cash';

  const [loading, setLoading] = useState(false);
  const [paymentMode, setPaymentMode] = useState('single'); // 'single' | 'split'
  const [singleMethod, setSingleMethod] = useState(firstAvailableMethod); // 'cash' | 'transfer' | 'qris' | 'edc'
  const [singleBank, setSingleBank] = useState(defaultBank);
  const [alasan, setAlasan] = useState('');

  const [splitMethods, setSplitMethods] = useState([
    { id: 'cash', label: 'Cash / Tunai', nama_bank: '', nominal: '' },
    { id: 'transfer', label: 'Transfer Bank', nama_bank: defaultBank, nominal: '' },
    { id: 'qris', label: 'QRIS', nama_bank: '', nominal: '' },
    { id: 'edc', label: 'Debit / EDC', nama_bank: defaultBank, nominal: '' },
  ]);

  const targetPrice = Math.round(Number(penjualan?.total_harga || penjualan?.harga_jual || 0));

  // Inisialisasi state saat modal dibuka atau data penjualan berubah
  useEffect(() => {
    if (!isOpen || !penjualan) return;

    setAlasan('');
    const rawPayments = Array.isArray(penjualan?.payments) && penjualan.payments.length > 0
      ? penjualan.payments
      : penjualan?.metode_pembayaran
        ? [{ metode_pembayaran: penjualan.metode_pembayaran, nama_bank: penjualan.nama_bank || '', nominal: targetPrice }]
        : [{ metode_pembayaran: firstAvailableMethod, nama_bank: '', nominal: targetPrice }];

    const activeList = rawPayments.filter((p) => (parseInt(p.nominal, 10) || 0) > 0);

    if (activeList.length <= 1 || availableMethodIds.length <= 1) {
      // Single Mode
      setPaymentMode('single');
      const first = activeList[0] || { metode_pembayaran: firstAvailableMethod, nama_bank: '' };
      const m = (first.metode_pembayaran || firstAvailableMethod).toLowerCase();
      const chosenMethod = availableMethodIds.includes(m) ? m : firstAvailableMethod;
      setSingleMethod(chosenMethod);
      setSingleBank(first.nama_bank || defaultBank);

      setSplitMethods([
        { id: 'cash', label: 'Cash / Tunai', nama_bank: '', nominal: chosenMethod === 'cash' ? targetPrice : '' },
        { id: 'transfer', label: 'Transfer Bank', nama_bank: chosenMethod === 'transfer' ? (first.nama_bank || defaultBank) : defaultBank, nominal: chosenMethod === 'transfer' ? targetPrice : '' },
        { id: 'qris', label: 'QRIS', nama_bank: '', nominal: chosenMethod === 'qris' ? targetPrice : '' },
        { id: 'edc', label: 'Debit / EDC', nama_bank: chosenMethod === 'edc' ? (first.nama_bank || defaultBank) : defaultBank, nominal: chosenMethod === 'edc' ? targetPrice : '' },
      ]);
    } else {
      // Split Mode
      setPaymentMode('split');
      const cashPay = activeList.find((p) => (p.metode_pembayaran || '').toLowerCase() === 'cash');
      const tfPay = activeList.find((p) => (p.metode_pembayaran || '').toLowerCase() === 'transfer');
      const qrisPay = activeList.find((p) => (p.metode_pembayaran || '').toLowerCase() === 'qris');
      const edcPay = activeList.find((p) => (p.metode_pembayaran || '').toLowerCase() === 'edc');

      setSplitMethods([
        { id: 'cash', label: 'Cash / Tunai', nama_bank: '', nominal: cashPay ? (parseInt(cashPay.nominal, 10) || '') : '' },
        { id: 'transfer', label: 'Transfer Bank', nama_bank: tfPay?.nama_bank || defaultBank, nominal: tfPay ? (parseInt(tfPay.nominal, 10) || '') : '' },
        { id: 'qris', label: 'QRIS', nama_bank: '', nominal: qrisPay ? (parseInt(qrisPay.nominal, 10) || '') : '' },
        { id: 'edc', label: 'Debit / EDC', nama_bank: edcPay?.nama_bank || defaultBank, nominal: edcPay ? (parseInt(edcPay.nominal, 10) || '') : '' },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, penjualan?.id, penjualan?.kode_trx]);

  // Mode Switch Helpers
  const handleSwitchToSingle = () => {
    setPaymentMode('single');
  };

  const handleSwitchToSplit = () => {
    setPaymentMode('split');
    // Jika split belum ada isian sama sekali, inisialisasi dengan metode single saat ini
    const currentTotalSplit = splitMethods.reduce((sum, p) => sum + (parseInt(p.nominal, 10) || 0), 0);
    if (currentTotalSplit === 0) {
      setSplitMethods((prev) =>
        prev.map((p) =>
          p.id === singleMethod
            ? { ...p, nominal: targetPrice, nama_bank: (singleMethod === 'transfer' || singleMethod === 'edc') ? (singleBank || defaultBank) : '' }
            : { ...p, nominal: '' }
        )
      );
    }
  };

  // Kalkulasi Split
  const totalSplit = splitMethods
    .filter((p) => effectivePaymentMethods[p.id] !== false)
    .reduce((sum, p) => sum + (parseInt(p.nominal, 10) || 0), 0);
  const sisaSplit = targetPrice - totalSplit;

  // Active Payments
  const activePayments = paymentMode === 'single'
    ? [
        {
          metode_pembayaran: singleMethod,
          nama_bank: (singleMethod === 'transfer' || singleMethod === 'edc') ? singleBank : '',
          nominal: targetPrice,
        },
      ]
    : splitMethods
        .filter((p) => effectivePaymentMethods[p.id] !== false && (parseInt(p.nominal, 10) || 0) > 0)
        .map((p) => ({
          metode_pembayaran: p.id,
          nama_bank: (p.id === 'transfer' || p.id === 'edc') ? (p.nama_bank || defaultBank) : '',
          nominal: parseInt(p.nominal, 10) || 0,
        }));

  const totalPembayaranSaatIni = paymentMode === 'single' ? targetPrice : totalSplit;
  const isBalance = paymentMode === 'single' ? true : sisaSplit === 0;

  // Handle Perubahan Split Method
  const handleSplitChange = (methodId, field, rawValue) => {
    let cleanVal = rawValue;
    if (field === 'nominal') {
      cleanVal = rawValue.replace(/\D/g, '');
    }

    setSplitMethods((prev) =>
      prev.map((p) => (p.id === methodId ? { ...p, [field]: cleanVal } : p))
    );
  };

  // Auto Isi Sisa Nominal pada Metode Tertentu
  const handleFillRemaining = (methodId) => {
    const otherTotal = splitMethods
      .filter((p) => effectivePaymentMethods[p.id] !== false)
      .reduce((sum, p) => {
        if (p.id !== methodId) {
          return sum + (parseInt(p.nominal, 10) || 0);
        }
        return sum;
      }, 0);

    const remaining = targetPrice - otherTotal;
    const newNominal = remaining > 0 ? remaining : 0;

    setSplitMethods((prev) =>
      prev.map((p) => (p.id === methodId ? { ...p, nominal: newNominal } : p))
    );
  };

  // Reset nominal slot split
  const handleResetSplit = (methodId) => {
    setSplitMethods((prev) =>
      prev.map((p) => (p.id === methodId ? { ...p, nominal: '' } : p))
    );
  };

  // Submit Perubahan
  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    if (!penjualan?.id) {
      toast.error('Data penjualan tidak valid');
      return;
    }

    if (!isBalance || totalPembayaranSaatIni !== targetPrice) {
      toast.error(`Total rincian pembayaran belum seimbang dengan total harga transaksi (Rp ${numberWithCommas(targetPrice)})!`);
      return;
    }

    if (paymentMode === 'single' && (singleMethod === 'transfer' || singleMethod === 'edc') && !singleBank) {
      toast.error('Silakan pilih rekening/nama bank terlebih dahulu');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        payments: activePayments,
        alasan_koreksi: alasan.trim() || 'Koreksi metode pembayaran',
      };

      const res = await http.put(`/penjualan/${penjualan.id}/payment`, payload, {
        headers: { Authorization: getAuthorizationHeader() },
      });

      toast.success(res.data?.message || 'Metode pembayaran berhasil dikoreksi!');
      if (onSuccess) {
        onSuccess(res.data?.data || res.data);
      }
      onClose();
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Gagal menyimpan perubahan metode pembayaran';
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[99999]" onClose={() => !loading && onClose()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs z-[99999]" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto z-[99999]">
          <div className="flex min-h-full items-center justify-center p-3 sm:p-4 text-center pointer-events-none">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="pointer-events-auto w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-slate-900 text-left align-middle shadow-2xl transition-all border border-slate-200/80 dark:border-slate-800 flex flex-col max-h-[92vh]">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-800">
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
                      <IconReceipt2 size={22} />
                    </div>
                    <div>
                      <Dialog.Title as="h3" className="text-base font-bold text-slate-800 dark:text-slate-100">
                        Koreksi Metode Pembayaran
                      </Dialog.Title>
                      <p className="text-xs text-slate-400 dark:text-slate-500">
                        No. Faktur: <span className="font-semibold text-slate-700 dark:text-slate-300">{penjualan?.kode_trx || '-'}</span>
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                  >
                    <IconX size={18} />
                  </button>
                </div>

                {/* Body Content */}
                <div className="p-5 overflow-y-auto space-y-4">
                  {/* Banner Target Harga (Fixed Total) */}
                  <div className="p-3.5 rounded-xl bg-gradient-to-r from-amber-50 to-amber-100/50 dark:from-amber-950/30 dark:to-amber-900/20 border border-amber-200/70 dark:border-amber-800/60 flex items-center justify-between">
                    <div>
                      <span className="text-[11px] font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wider block">
                        Total Tagihan Transaksi
                      </span>
                      <span className="text-lg sm:text-xl font-black text-amber-950 dark:text-amber-200">
                        Rp {numberWithCommas(targetPrice)}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-[11px] text-slate-500 dark:text-slate-400 block">Kode Barang</span>
                      <span className="text-xs font-bold text-slate-800 dark:text-slate-200">
                        {penjualan?.item?.kode_item || '-'}
                      </span>
                    </div>
                  </div>

                  {/* Mode Selector (Single vs Split) - Hanya muncul jika lebih dari 1 metode aktif */}
                  {availableMethodIds.length > 1 && (
                    <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 dark:bg-slate-800/80 rounded-xl">
                      <button
                        type="button"
                        onClick={handleSwitchToSingle}
                        className={`py-2 text-xs sm:text-sm font-bold rounded-lg transition-all cursor-pointer ${
                          paymentMode === 'single'
                            ? 'bg-white dark:bg-slate-700 text-amber-600 dark:text-amber-400 shadow-xs'
                            : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                        }`}
                      >
                        1 Metode Penuh (100%)
                      </button>
                      <button
                        type="button"
                        onClick={handleSwitchToSplit}
                        className={`py-2 text-xs sm:text-sm font-bold rounded-lg transition-all cursor-pointer ${
                          paymentMode === 'split'
                            ? 'bg-white dark:bg-slate-700 text-amber-600 dark:text-amber-400 shadow-xs'
                            : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                        }`}
                      >
                        Bagi Pembayaran (Split)
                      </button>
                    </div>
                  )}

                  {/* FORM SINGLE MODE */}
                  {paymentMode === 'single' && (
                    <div className="space-y-3 pt-1">
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                        Pilih Metode Pembayaran Penuh
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {[
                          { id: 'cash', label: 'Cash / Tunai', icon: IconCash, color: 'text-emerald-500' },
                          { id: 'transfer', label: 'Transfer Bank', icon: IconBuildingBank, color: 'text-blue-500' },
                          { id: 'qris', label: 'QRIS', icon: IconQrcode, color: 'text-orange-500' },
                          { id: 'edc', label: 'Debit / EDC', icon: IconCreditCard, color: 'text-purple-500' },
                        ]
                          .filter((m) => effectivePaymentMethods[m.id] !== false)
                          .map((m) => {
                          const Icon = m.icon;
                          const active = singleMethod === m.id;
                          return (
                            <button
                              key={m.id}
                              type="button"
                              onClick={() => setSingleMethod(m.id)}
                              className={`p-3 rounded-xl border flex flex-col items-center gap-1.5 transition-all cursor-pointer text-center ${
                                active
                                  ? 'border-amber-500 bg-amber-500/10 text-amber-700 dark:text-amber-300 ring-2 ring-amber-500/30'
                                  : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 text-slate-600 dark:text-slate-300 hover:border-slate-300'
                              }`}
                            >
                              <Icon size={20} className={active ? 'text-amber-600' : m.color} />
                              <span className="text-xs font-semibold">{m.label}</span>
                            </button>
                          );
                        })}
                      </div>

                      {/* Dropdown Bank untuk Transfer / EDC */}
                      {(singleMethod === 'transfer' || singleMethod === 'edc') && (
                        <div className="p-3.5 rounded-xl border border-blue-200/80 dark:border-blue-800/60 bg-blue-50/40 dark:bg-blue-950/20 space-y-2">
                          <label className="text-xs font-bold text-blue-900 dark:text-blue-200 flex items-center gap-1.5">
                            <IconBuildingBank size={15} />
                            Pilih Rekening / Mesin Bank
                          </label>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {bankList.map((b) => (
                              <button
                                key={b}
                                type="button"
                                onClick={() => setSingleBank(b)}
                                className={`px-2.5 py-2 rounded-lg text-xs font-bold border flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                                  singleBank === b
                                    ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                                    : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-blue-400'
                                }`}
                              >
                                <BankLogo bankName={b} className="h-3.5 w-auto" />
                                <span>{b}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* FORM SPLIT MODE */}
                  {paymentMode === 'split' && (
                    <div className="space-y-3 pt-1">
                      {/* Status Balance Pill */}
                      <div
                        className={`p-3 rounded-xl border flex items-center justify-between text-xs sm:text-sm font-semibold transition-all ${
                          isBalance
                            ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200'
                            : sisaSplit > 0
                              ? 'bg-rose-50 dark:bg-rose-950/30 border-rose-300 dark:border-rose-800 text-rose-800 dark:text-rose-200'
                              : 'bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-200'
                        }`}
                      >
                        <div className="flex items-center gap-1.5">
                          {isBalance ? (
                            <>
                              <IconCheck size={18} className="text-emerald-600 dark:text-emerald-400" />
                              <span>Total Pas (Seimbang)</span>
                            </>
                          ) : (
                            <>
                              <IconAlertTriangle size={18} />
                              <span>
                                {sisaSplit > 0 ? `Kurang Rp ${numberWithCommas(sisaSplit)}` : `Kelebihan Rp ${numberWithCommas(Math.abs(sisaSplit))}`}
                              </span>
                            </>
                          )}
                        </div>
                        <span>Total: Rp {numberWithCommas(totalSplit)}</span>
                      </div>

                      {/* Inputs Per Metode */}
                      <div className="space-y-2.5">
                        {splitMethods
                          .filter((method) => effectivePaymentMethods[method.id] !== false)
                          .map((method) => {
                          const isBankMethod = method.id === 'transfer' || method.id === 'edc';
                          return (
                            <div
                              key={method.id}
                              className="p-3 rounded-xl border border-slate-200 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-800/40 space-y-2"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  {method.id === 'cash' && <IconCash size={17} className="text-emerald-500" />}
                                  {method.id === 'transfer' && <IconBuildingBank size={17} className="text-blue-500" />}
                                  {method.id === 'qris' && <IconQrcode size={17} className="text-orange-500" />}
                                  {method.id === 'edc' && <IconCreditCard size={17} className="text-purple-500" />}
                                  <span className="text-xs font-bold text-slate-800 dark:text-slate-200">
                                    {method.label}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => handleFillRemaining(method.id)}
                                    className="px-2 py-0.5 rounded-md text-[11px] font-bold bg-amber-500/10 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20 transition-all cursor-pointer"
                                    title="Isi sisa tagihan ke metode ini"
                                  >
                                    + Sisa
                                  </button>
                                  {(parseInt(method.nominal, 10) || 0) > 0 && (
                                    <button
                                      type="button"
                                      onClick={() => handleResetSplit(method.id)}
                                      className="p-1 rounded-md text-slate-400 hover:text-rose-500 transition-colors"
                                      title="Kosongkan"
                                    >
                                      <IconX size={14} />
                                    </button>
                                  )}
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                <div className="relative flex-1">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                                    Rp
                                  </span>
                                  <input
                                    type="text"
                                    placeholder="0"
                                    value={method.nominal ? numberWithCommas(method.nominal) : ''}
                                    onChange={(e) => handleSplitChange(method.id, 'nominal', e.target.value)}
                                    className="w-full pl-9 pr-3 py-1.5 text-xs sm:text-sm font-bold bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                                  />
                                </div>

                                {isBankMethod && (
                                  <select
                                    value={method.nama_bank || defaultBank}
                                    onChange={(e) => handleSplitChange(method.id, 'nama_bank', e.target.value)}
                                    className="px-2.5 py-1.5 text-xs font-bold bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 shrink-0"
                                  >
                                    {bankList.map((b) => (
                                      <option key={b} value={b}>
                                        {b}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Alasan Koreksi */}
                  <div className="space-y-1 pt-1">
                    <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 flex items-center gap-1">
                      <IconInfoCircle size={14} />
                      Alasan Koreksi (Opsional):
                    </label>
                    <input
                      type="text"
                      placeholder="Contoh: Salah pilih kasir, seharusnya split BCA"
                      value={alasan}
                      onChange={(e) => setAlasan(e.target.value)}
                      className="w-full px-3 py-2 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-slate-100 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                    />
                  </div>
                </div>

                {/* Footer Buttons */}
                <div className="flex items-center justify-end gap-2 px-5 py-3.5 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className="px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all cursor-pointer"
                  >
                    Batal
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading || !isBalance || totalPembayaranSaatIni !== targetPrice}
                    className={`px-5 py-2 rounded-xl text-xs sm:text-sm font-bold flex items-center gap-1.5 transition-all shadow-xs cursor-pointer ${
                      isBalance && totalPembayaranSaatIni === targetPrice && !loading
                        ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-amber-500/20'
                        : 'bg-slate-200 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    {loading ? (
                      <span className="flex items-center gap-1.5">
                        <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Menyimpan...
                      </span>
                    ) : (
                      <>
                        <IconCheck size={16} />
                        Simpan Perubahan
                      </>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
